import { ArrowUp, Mail } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { Reveal } from "./shared";
import { useLanguage } from "@/i18n/LanguageContext";
import { CONTACT_EMAIL } from "@/i18n/contactDetails";
import { BrandLockup } from "./LogoMark";

export const Footer = () => {
    const { t } = useLanguage();
    const links = [
        { label: t.nav.manifesto, href: "#manifesto", testid: "footer-link-manifesto" },
        { label: t.nav.services, href: "#services", testid: "footer-link-services" },
        { label: t.nav.process, href: "#process", testid: "footer-link-process" },
        { label: t.nav.contact, href: "#contact", testid: "footer-link-contact" },
    ];
    return (
        <footer data-testid="site-footer" className="relative overflow-hidden border-t border-white/10">
            <div className="mx-auto max-w-[1400px] px-6 pt-20 md:px-12">
                <div className="grid grid-cols-1 gap-12 pb-20 md:grid-cols-12">
                    <div className="md:col-span-5">
                        <Reveal>
                            <BrandLockup emblemClass="h-14 w-14" textClass="text-2xl" align="start" />
                            <p className="mt-4 max-w-xs text-sm leading-relaxed text-ash">
                                {t.footer.tagline}
                            </p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-3">
                        <Reveal delay={0.1}>
                            <p className="text-[11px] uppercase tracking-[0.35em] text-ash">
                                {t.footer.contactLabel}
                            </p>
                            <a
                                data-testid="footer-email-link"
                                href={`mailto:${CONTACT_EMAIL}`}
                                className="mt-5 flex w-fit items-center gap-2 text-sm text-bone underline decoration-gold/40 underline-offset-8 transition-colors hover:text-gold"
                            >
                                <Mail className="h-3.5 w-3.5 text-gold" />
                                {CONTACT_EMAIL}
                            </a>
                            <p className="mt-4 text-sm text-ash">{t.footer.location}</p>
                        </Reveal>
                    </div>
                    <div className="md:col-span-3">
                        <Reveal delay={0.15}>
                            <p className="text-[11px] uppercase tracking-[0.35em] text-ash">
                                {t.footer.exploreLabel}
                            </p>
                            <div className="mt-5 flex flex-col gap-3">
                                {links.map((l) => (
                                    <button
                                        key={l.href}
                                        data-testid={l.testid}
                                        onClick={() => scrollToId(l.href)}
                                        className="w-fit text-sm text-bone transition-colors hover:text-gold"
                                    >
                                        {l.label}
                                    </button>
                                ))}
                                <a
                                    href="/consultancy"
                                    data-testid="footer-link-consultancy"
                                    className="w-fit text-sm text-bone transition-colors hover:text-gold"
                                >
                                    Consultancy
                                </a>
                            </div>
                        </Reveal>
                    </div>
                    <div className="flex items-start md:col-span-1 md:justify-end">
                        <Reveal delay={0.2}>
                            <button
                                data-testid="footer-back-to-top"
                                onClick={() => scrollToId("#top")}
                                aria-label="Back to top"
                                className="group border border-white/15 p-4 transition-colors hover:border-gold/60"
                            >
                                <ArrowUp className="h-4 w-4 text-ash transition-colors group-hover:text-gold" />
                            </button>
                        </Reveal>
                    </div>
                </div>
            </div>
            <div className="pointer-events-none select-none px-2" aria-hidden="true">
                <p className="text-stroke-faint text-center font-display text-[19vw] font-medium leading-[0.8] tracking-tight">
                    CLARKE
                </p>
            </div>
            <div className="border-t border-white/10">
                <div className="mx-auto flex max-w-[1400px] flex-col gap-2 px-6 py-6 text-[11px] uppercase tracking-[0.25em] text-ash sm:flex-row sm:items-center sm:justify-between md:px-12">
                    <span data-testid="footer-copyright">{t.footer.copyright}</span>
                    <span data-testid="footer-kvk" className="text-ash/60">{t.footer.kvk}</span>
                    <span className="flex flex-wrap items-center gap-5">
                        <a href="/privacy" data-testid="footer-privacy-link" className="transition-colors hover:text-gold">
                            Privacy
                        </a>
                    </span>
                    <span>{t.footer.motto}</span>
                </div>
            </div>
        </footer>
    );
};
